import { Loader, PilledTabs, Text as Tx, } from "@bracket/ui";
import { AssetValue, TimeWall, } from "@components";
import { PieChart,} from "@sections/BrktETHPage/components/PieChart";
import { SwapCards, } from "@sections/BrktETHPage/components/SwapCards";
import { useSwap, useVaults, } from "@store";
import type { FunctionComponent, } from "react";
import styled from "styled-components";
import { BalanceCard, PlatformCardItem, } from "./components";


/**
 * BorealSection component displays the ETH+ DeFi Yield Strategy management interface
 * Shows platform metrics including:
 * - Next epoch timing
 * - Vault variance
 * - Total withdrawals
 * - Pending deposits
 * - Estimated amount due
 * - Collateral balances
 * - Manager balances
 * 
 * Also provides swap functionality between tokens with automatic reversal on unmount
 */
export const BorealSection: FunctionComponent = () => {

	const { toggleMode, mode, } = useSwap();
	const { activeAdminVault: vault, } = useVaults();

	if(!vault)
		return <Loader />;

	const {
		title,
		mgmt: {
			estAmountDue,
			managerBalances,
			nextEpochStartTime,
			pendingDeposits,
			totalWithdrawals,
			totalWithdrawalsNext,
			vaultVariance,
			navFees,
			currentEpochClosable,
		},
		denomination,
		denomination: {
			symbol,
		},
	} = vault;


	return (
		<>
			<Tx size="xl" color="title">
				{title}
			</Tx>

			<ContentContainer>
				<CardContainer $gap={8} $disabled={false}>
					<Tx size="xl" color="title">
						Platform
					</Tx>

					<PlatformItemsContainer>
						<PlatformCardItem
							label="Next Epoch End"
							tooltip="Time until next epoch"
							highlight="green"
							value={
								<AssetCont>
									<TimeWall time={nextEpochStartTime} />
								</AssetCont>
							}
						/>

						<PlatformCardItem label="Pending Deposits" tooltip="Current pending deposits"
								value={<AssetCont><AssetValue size="lg" color="subheading" value={pendingDeposits.toString()} token={denomination} /></AssetCont> } />

						<PlatformCardItem
							label="Pending Withdrawals"
							tooltip="Amount for next withdrawal"
							value={
								<AssetCont>
									<AssetValue
										value={totalWithdrawals.toString()}
										token={denomination}
										size="lg"
										color="subheading"
									/>
								</AssetCont>
							}
						/>
						<PlatformCardItem
							label="Fixed Fees"
							tooltip="Associated fixed fees to the vault"
							value={
								<AssetCont>
									<AssetValue
										value={navFees.toString()}
										token={denomination}
										size="lg"
										color="subheading"
									/>
								</AssetCont>
							}
						/>
						<PlatformCardItem
							label="Net Flow"
							tooltip="Estimated net flows"
							value={
								<AssetCont>
									<AssetValue
										value={estAmountDue.toString()}
										token={denomination}
										size="lg"
										color="subheading"
									/>
								</AssetCont>
							}
						/>
						<PlatformCardItem
							label="VAULT RESOLVE ACTION"
							tooltip="Amount required to settle"
							color={currentEpochClosable ? "surface_on" : "error"}
							value={
								<AssetCont>
								{
									currentEpochClosable
										? <Tx size="lg" bold color="primary" title={`Variance: ${(BigInt(vaultVariance) / BigInt(10 ** denomination.decimals)).toString()}`}>Adequately Funded 👍</Tx>
										: <AssetValue bold value={vaultVariance.toString()} token={denomination} size="lg" color="error" />
								}
								</AssetCont>
							}
							bold
						/>
						<PlatformCardItem
							label="Subsequent Withdrawals"
							tooltip="Withdrawals scheduled for the next cycle"
							value={
								<AssetCont>
									<AssetValue
										value={totalWithdrawalsNext.toString()}
										token={denomination}
										size="lg"
										color="subheading"
									/>
								</AssetCont>
							}
						/>
					</PlatformItemsContainer>
				</CardContainer>

				<CardContainer $gap={32}>
					<Tx size="xl" color="title">Wallet balances</Tx>
					<BalanceItemsContainer $columns={symbol === "USDC" ? "1fr 2fr" : "1fr 1fr 1.5fr"}>
						<Tx size="lg">
							Asset
						</Tx>
						<Tx size="lg" justify="flex-end">
							Balance
						</Tx>
						{
							symbol !== "USDC" && (
								<Tx size="lg" justify="flex-end">
									Conv. Rate (ETH)
								</Tx>
							)
						}
						{
							managerBalances.map(item => {
								return <BalanceCard key={item.title} {...item} />;
							})
						}
					</BalanceItemsContainer>
				</CardContainer>

				<SwapContainer $disabled={symbol !== "BRKTETH"}>
					<div style={{ width: "155px", margin: "1rem 0" }}>
						<PilledTabs
							items={[
								{ label: "Buy", id: "buy" },
								{ label: "Redeem", id: "redeem" },
							]}
							activeTab={mode === "REDEEM" ? "redeem" : "buy"}
							onTabChange={() => toggleMode()}
							containerStyle={{ position: "relative", alignSelf: "flex-start" }}
						/>
					</div>
					<SwapCards />
				</SwapContainer>

				<CardContainer $gap={8} $disabled={symbol !== "BRKTETH"}>
					<div style={{ paddingTop: "50px" }}>
						<PieChart height={400} />
					</div>{" "}
				</CardContainer>

			</ContentContainer>
		</>
	);
};


const ContentContainer = styled.div`
	display: grid;
	grid-template-rows: repeat(2, min-content);
	gap: 32px;
	@media (min-width: 768px) {
		grid-template-columns: repeat(2, 1fr);
		grid-template-rows: 1fr;
		gap: 24px;
	}
`;

const CardContainer = styled.div<{ $gap: number; $disabled?: boolean; }>`
	display:  ${({ $disabled }) => $disabled ? "none" : "flex"};
	flex-direction: column;
	padding: 24px;
	gap: ${({ $gap }) => $gap}px;
	border-radius: 8px;
	background-color: var(--surface);
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	opacity: ${({ $disabled }) => $disabled ? 0 : 1};
	pointer-events: ${({ $disabled }) => $disabled ? "none" : "auto"};
`;

const PlatformItemsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const BalanceItemsContainer = styled.div<{ $columns: string; }>`
	display: grid;
	grid-template-columns: ${({ $columns }) => $columns};
	gap: 24px 32px;
	align-items: center;
`;

const SwapContainer = styled.div<{ $disabled: boolean; }>`
	display:  ${({ $disabled }) => $disabled ? "none" : "flex"};
	flex-direction: column;
	gap: 8px;
	opacity: ${({ $disabled }) => $disabled ? 0 : 1};
	pointer-events: ${({ $disabled }) => $disabled ? "none" : "auto"};
`;

const AssetCont = styled.div`
	display: flex;
	flex-direction: column;
	text-align: right;
`;

